import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Alert, useTheme, useMediaQuery } from '@mui/material';
import { VisibilityOutlined, Close as CloseIcon } from '@mui/icons-material';
import { createLogger } from '@/utils/logger';
import { impersonationStore } from '@/stores/impersonationStore';
import { IMPERSONATION_BANNER_HEIGHT_VAR } from '@/constants/layout';

const log = createLogger('ImpersonationBanner');

/**
 * Formats remaining time in seconds to MM:SS display format
 */
function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return '0:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * ImpersonationBanner Component
 *
 * Displays a warning banner when a preparer is viewing the portal
 * as an impersonated client. Shows session timer and restrictions notice.
 *
 * The banner:
 * - Only renders when impersonation is active
 * - Shows countdown timer for session expiration
 * - Provides "Close Tab" action to end impersonation session
 * - Auto-closes tab when session expires
 */
export function ImpersonationBanner(): React.ReactElement | null {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Track time remaining in seconds (default to 0 if not impersonated)
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    const remaining = impersonationStore.getTimeRemaining();
    log.debug('Initial time remaining', { remaining });
    return remaining ?? 0;
  });

  // Check if impersonation is active
  const [isImpersonated, setIsImpersonated] = useState<boolean>(() => {
    return impersonationStore.isImpersonated();
  });

  // Get preparer info from impersonation data (static for session lifetime)
  const [preparerDisplayName] = useState<string>(() => {
    const data = impersonationStore.getImpersonation();
    return data?.preparerDisplayName || 'Unknown';
  });

  /**
   * Handle session expiration - clear impersonation and close tab
   */
  const handleSessionExpired = useCallback(() => {
    log.info('Impersonation session expired');
    impersonationStore.clearImpersonation();

    // Attempt to close the tab (may not work if tab wasn't opened via script)
    window.close();

    // If window.close() didn't work (common in modern browsers),
    // redirect to login as fallback
    setTimeout(() => {
      log.warn('Unable to close tab, redirecting to login');
      window.location.href = '/login';
    }, 100);
  }, []);

  /**
   * Handle manual close tab action
   */
  const handleCloseTab = useCallback(() => {
    log.info('User requested to close impersonation tab');
    impersonationStore.clearImpersonation();
    window.close();

    // Fallback if window.close() doesn't work
    setTimeout(() => {
      log.warn('Unable to close tab, redirecting to login');
      window.location.href = '/login';
    }, 100);
  }, []);

  // Update timer every second
  useEffect(() => {
    // Don't run timer if not impersonated
    if (!isImpersonated) {
      return;
    }

    const intervalId = setInterval(() => {
      const remaining = impersonationStore.getTimeRemaining();

      if (remaining === null || remaining <= 0) {
        handleSessionExpired();
        clearInterval(intervalId);
        return;
      }

      setTimeRemaining(remaining);

      // Check if impersonation was cleared externally
      if (!impersonationStore.isImpersonated()) {
        log.debug('Impersonation cleared externally');
        setIsImpersonated(false);
        clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isImpersonated, handleSessionExpired]);

  // Full-height mobile pages subtract this, since the banner sits in the layout's flow above them.
  const bannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bannerRef.current;
    if (!isImpersonated || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(IMPERSONATION_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    const observer = new ResizeObserver(publish);
    publish();
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(IMPERSONATION_BANNER_HEIGHT_VAR);
    };
  }, [isImpersonated]);

  // Don't render if not impersonated
  if (!isImpersonated) {
    return null;
  }

  return (
    <Alert
      ref={bannerRef}
      severity="warning"
      icon={<VisibilityOutlined />}
      sx={{
        borderRadius: 0,
        py: { xs: 1, sm: 1.5 },
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        '& .MuiAlert-icon': {
          color: theme.palette.warning.dark,
          alignItems: 'center',
        },
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          width: '100%',
        }}
      >
        {/* Main content */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
              color: theme.palette.warning.dark,
              display: 'block',
            }}
          >
            Viewing as impersonated client
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: 400,
                mx: 1,
                color: theme.palette.warning.dark,
              }}
            >
              •
            </Typography>
            Impersonated by {preparerDisplayName}
            {!isMobile && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: 600,
                  ml: 1,
                  color: theme.palette.warning.dark,
                }}
              >
                - {formatTimeRemaining(timeRemaining)} remaining
              </Typography>
            )}
          </Typography>

          {/* Show timer on separate line for mobile */}
          {isMobile && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.warning.dark,
                mt: 0.5,
              }}
            >
              {formatTimeRemaining(timeRemaining)} remaining
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{
              color: theme.palette.warning.dark,
              opacity: 0.85,
              display: 'block',
              mt: 0.5,
            }}
          >
            Some actions (like signing documents) are restricted
          </Typography>
        </Box>

        {/* Close Tab button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloseIcon />}
          onClick={handleCloseTab}
          sx={{
            borderColor: theme.palette.warning.dark,
            color: theme.palette.warning.dark,
            backgroundColor: 'transparent',
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            '&:hover': {
              borderColor: theme.palette.warning.dark,
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.common.white,
            },
          }}
        >
          Close Tab
        </Button>
      </Box>
    </Alert>
  );
}

export default ImpersonationBanner;
